/**
 * Shared audio sample library — CC0 sourced .ogg files served from
 * `/audio/<name>.ogg` on the static origin. Falls back to procedural
 * synthesis if a sample is missing or fails to decode (so the API
 * remains stable while real samples are dropped in incrementally).
 *
 * Phase 6.1 ships the API + procedural fallbacks. Real samples can be
 * dropped into `web/public/audio/` later without code changes.
 *
 * Usage:
 *   import { audioLib } from '@tt/audio-lib';
 *   await audioLib.preload(['tap-pop', 'fanfare-short']);
 *   audioLib.play('tap-pop', { volume: 0.6 });
 *   const stop = audioLib.loop('wood-creak', { volume: 0.3 });
 *   stop();
 */

export type SampleName =
  // UI
  | 'button-click' | 'button-hover' | 'toast-in' | 'toast-error' | 'success-chime'
  // Game-action
  | 'tap-pop' | 'jump-grunt' | 'hit-thud' | 'weapon-clank' | 'card-flip'
  | 'coin-place' | 'candle-snuff' | 'glass-shatter'
  // Pickups / results
  | 'coin-jingle' | 'power-up' | 'fanfare-short' | 'fanfare-long' | 'fail-sad' | 'gasp'
  // Ambient loops
  | 'wood-creak' | 'wind-howl' | 'conveyor-hum' | 'seance-whispers' | 'heartbeat'
  // Sigs
  | 'bell' | 'gong' | 'magic-chime' | 'planchette-scrape' | 'drumroll' | 'victory-horn';

interface SampleSpec {
  /** Path to the .ogg file (relative to static origin). */
  url: string;
  /** Base volume multiplier. */
  defaultVolume: number;
  /** Procedural fallback emitter when the file fails to load. */
  fallback: (ctx: AudioContext, dest: AudioNode) => void;
}

function tone(ctx: AudioContext, dest: AudioNode, freq: number, durMs: number, vol = 0.15, type: OscillatorType = 'sine'): void {
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.type = type;
  o.frequency.value = freq;
  const t = ctx.currentTime;
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(vol, t + 0.005);
  g.gain.exponentialRampToValueAtTime(0.0001, t + durMs / 1000);
  o.connect(g);
  g.connect(dest);
  o.start(t);
  o.stop(t + durMs / 1000 + 0.05);
}
function noise(ctx: AudioContext, dest: AudioNode, durMs: number, vol = 0.08): void {
  const len = Math.max(1, Math.floor(ctx.sampleRate * (durMs / 1000)));
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / data.length, 2);
  const src = ctx.createBufferSource();
  src.buffer = buf;
  // Shape it — this is the PERMANENT playback path (no .ogg samples exist
  // in web/public/audio/ yet, so every wind-howl/gasp/candle-snuff/
  // planchette-scrape/drumroll call in production hits this fallback, not
  // a rare degraded case), and it was raw full-spectrum white noise with
  // no filtering — read as loud, harsh static.
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 2200;
  lp.Q.value = 0.5;
  const g = ctx.createGain();
  const t = ctx.currentTime;
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(vol, t + 0.008);
  src.connect(lp).connect(g);
  g.connect(dest);
  src.start(t);
}

const SPECS: Record<SampleName, SampleSpec> = {
  'button-click':   { url: '/audio/button-click.ogg',   defaultVolume: 0.6, fallback: (c, d) => tone(c, d, 220, 40, 0.05) },
  'button-hover':   { url: '/audio/button-hover.ogg',   defaultVolume: 0.3, fallback: (c, d) => tone(c, d, 440, 20, 0.02) },
  'toast-in':       { url: '/audio/toast-in.ogg',       defaultVolume: 0.5, fallback: (c, d) => tone(c, d, 1175, 80, 0.08, 'triangle') },
  'toast-error':    { url: '/audio/toast-error.ogg',    defaultVolume: 0.6, fallback: (c, d) => tone(c, d, 196, 220, 0.14, 'sawtooth') },
  'success-chime':  { url: '/audio/success-chime.ogg',  defaultVolume: 0.7, fallback: (c, d) => { tone(c, d, 523, 80); tone(c, d, 784, 200, 0.16, 'triangle'); } },
  'tap-pop':        { url: '/audio/tap-pop.ogg',        defaultVolume: 0.6, fallback: (c, d) => tone(c, d, 880, 50, 0.08, 'square') },
  'jump-grunt':     { url: '/audio/jump-grunt.ogg',     defaultVolume: 0.7, fallback: (c, d) => { noise(c, d, 60, 0.06); tone(c, d, 280, 80, 0.08, 'square'); } },
  'hit-thud':       { url: '/audio/hit-thud.ogg',       defaultVolume: 0.8, fallback: (c, d) => { noise(c, d, 100, 0.12); tone(c, d, 110, 120, 0.1, 'sawtooth'); } },
  'weapon-clank':   { url: '/audio/weapon-clank.ogg',   defaultVolume: 0.7, fallback: (c, d) => { tone(c, d, 1500, 60, 0.1, 'square'); tone(c, d, 800, 80, 0.06, 'square'); } },
  'card-flip':      { url: '/audio/card-flip.ogg',      defaultVolume: 0.5, fallback: (c, d) => { noise(c, d, 80, 0.06); tone(c, d, 380, 70, 0.06, 'triangle'); } },
  'coin-place':     { url: '/audio/coin-place.ogg',     defaultVolume: 0.6, fallback: (c, d) => { tone(c, d, 880, 40, 0.06); tone(c, d, 1320, 80, 0.06, 'triangle'); } },
  'candle-snuff':   { url: '/audio/candle-snuff.ogg',   defaultVolume: 0.6, fallback: (c, d) => noise(c, d, 180, 0.10) },
  'glass-shatter':  { url: '/audio/glass-shatter.ogg',  defaultVolume: 0.7, fallback: (c, d) => { for (let i = 0; i < 4; i++) tone(c, d, 1800 + i * 600, 80, 0.04, 'triangle'); } },
  'coin-jingle':    { url: '/audio/coin-jingle.ogg',    defaultVolume: 0.6, fallback: (c, d) => { tone(c, d, 1320, 60, 0.08, 'triangle'); tone(c, d, 1760, 100, 0.06, 'triangle'); } },
  'power-up':       { url: '/audio/power-up.ogg',       defaultVolume: 0.7, fallback: (c, d) => { tone(c, d, 523, 60, 0.08); tone(c, d, 659, 60, 0.08); tone(c, d, 784, 60, 0.08); tone(c, d, 1047, 200, 0.10, 'triangle'); } },
  'fanfare-short':  { url: '/audio/fanfare-short.ogg',  defaultVolume: 0.7, fallback: (c, d) => { const t = c.currentTime; for (let i = 0; i < 4; i++) tone(c, d, [523, 659, 784, 1047][i]!, 200, 0.16, 'triangle'); void t; } },
  'fanfare-long':   { url: '/audio/fanfare-long.ogg',   defaultVolume: 0.8, fallback: (c, d) => { for (let i = 0; i < 6; i++) tone(c, d, [523, 587, 659, 784, 880, 1047][i]!, 220, 0.16, 'triangle'); } },
  'fail-sad':       { url: '/audio/fail-sad.ogg',       defaultVolume: 0.6, fallback: (c, d) => { tone(c, d, 392, 220, 0.14, 'sawtooth'); tone(c, d, 311, 320, 0.16, 'sawtooth'); } },
  'gasp':           { url: '/audio/gasp.ogg',           defaultVolume: 0.5, fallback: (c, d) => noise(c, d, 220, 0.08) },
  'wood-creak':     { url: '/audio/wood-creak.ogg',     defaultVolume: 0.3, fallback: (c, d) => { tone(c, d, 110, 600, 0.06, 'sawtooth'); tone(c, d, 180, 800, 0.04, 'sawtooth'); } },
  'wind-howl':      { url: '/audio/wind-howl.ogg',      defaultVolume: 0.4, fallback: (c, d) => noise(c, d, 1200, 0.08) },
  'conveyor-hum':   { url: '/audio/conveyor-hum.ogg',   defaultVolume: 0.3, fallback: (c, d) => tone(c, d, 80, 800, 0.06, 'sawtooth') },
  'seance-whispers':{ url: '/audio/seance-whispers.ogg',defaultVolume: 0.4, fallback: (c, d) => noise(c, d, 1500, 0.05) },
  'heartbeat':      { url: '/audio/heartbeat.ogg',      defaultVolume: 0.6, fallback: (c, d) => { tone(c, d, 60, 80, 0.18, 'sine'); setTimeout(() => tone(c, d, 80, 100, 0.14, 'sine'), 120); } },
  'bell':           { url: '/audio/bell.ogg',           defaultVolume: 0.6, fallback: (c, d) => { tone(c, d, 1047, 120, 0.16, 'triangle'); setTimeout(() => tone(c, d, 1568, 80, 0.10, 'triangle'), 60); } },
  'gong':           { url: '/audio/gong.ogg',           defaultVolume: 0.7, fallback: (c, d) => { tone(c, d, 220, 800, 0.18, 'triangle'); tone(c, d, 110, 1200, 0.12, 'triangle'); } },
  'magic-chime':    { url: '/audio/magic-chime.ogg',    defaultVolume: 0.6, fallback: (c, d) => { tone(c, d, 1568, 80, 0.10, 'triangle'); tone(c, d, 1976, 120, 0.10, 'triangle'); tone(c, d, 2349, 200, 0.10, 'triangle'); } },
  'planchette-scrape':{ url: '/audio/planchette-scrape.ogg', defaultVolume: 0.5, fallback: (c, d) => noise(c, d, 200, 0.08) },
  'drumroll':       { url: '/audio/drumroll.ogg',       defaultVolume: 0.6, fallback: (c, d) => { for (let i = 0; i < 12; i++) setTimeout(() => noise(c, d, 40, 0.10), i * 50); } },
  'victory-horn':   { url: '/audio/victory-horn.ogg',   defaultVolume: 0.8, fallback: (c, d) => { tone(c, d, 392, 200, 0.16, 'triangle'); tone(c, d, 523, 200, 0.16, 'triangle'); tone(c, d, 659, 400, 0.18, 'triangle'); } },
};

const buffers = new Map<SampleName, AudioBuffer | 'failed'>();
const inflight = new Map<SampleName, Promise<void>>();

let ctxRef: AudioContext | null = null;
let busRef: AudioNode | null = null;

export const audioLib = {
  /** Wire to the host AudioContext + sfx bus from @tt/audio. Call once on unlock. */
  attach(ctx: AudioContext, bus: AudioNode): void {
    ctxRef = ctx;
    busRef = bus;
  },
  /** Preload a list of samples in parallel. Failures fall back silently. */
  async preload(names: SampleName[]): Promise<void> {
    if (!ctxRef) return;
    await Promise.all(names.map((n) => audioLib.load(n)));
  },
  async load(name: SampleName): Promise<void> {
    if (!ctxRef) return;
    if (buffers.has(name)) return;
    if (inflight.has(name)) return inflight.get(name);
    const spec = SPECS[name];
    const p = (async () => {
      try {
        const r = await fetch(spec.url);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const bytes = await r.arrayBuffer();
        const buf = await ctxRef!.decodeAudioData(bytes);
        buffers.set(name, buf);
      } catch {
        // Mark as failed so we don't retry forever; fallback synth is used.
        buffers.set(name, 'failed');
      } finally {
        inflight.delete(name);
      }
    })();
    inflight.set(name, p);
    return p;
  },
  play(name: SampleName, opts: { volume?: number; pan?: number } = {}): void {
    if (!ctxRef || !busRef) return;
    const spec = SPECS[name];
    const buf = buffers.get(name);
    if (!buf) {
      // Either not loaded or failed; use procedural fallback.
      spec.fallback(ctxRef, busRef);
      // Kick off load for next time (best-effort).
      void audioLib.load(name);
      return;
    }
    if (buf === 'failed') {
      spec.fallback(ctxRef, busRef);
      return;
    }
    const src = ctxRef.createBufferSource();
    src.buffer = buf;
    const g = ctxRef.createGain();
    g.gain.value = (opts.volume ?? 1) * spec.defaultVolume;
    if (typeof opts.pan === 'number') {
      const p = ctxRef.createStereoPanner();
      p.pan.value = Math.max(-1, Math.min(1, opts.pan));
      src.connect(g); g.connect(p); p.connect(busRef);
    } else {
      src.connect(g); g.connect(busRef);
    }
    src.start();
  },
  loop(name: SampleName, opts: { volume?: number } = {}): () => void {
    if (!ctxRef || !busRef) return () => {};
    const spec = SPECS[name];
    const buf = buffers.get(name);
    if (!buf || buf === 'failed') {
      // No-op for missing loops; the procedural fallback in @tt/audio drives ambient music.
      void audioLib.load(name);
      return () => {};
    }
    const src = ctxRef.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    const g = ctxRef.createGain();
    g.gain.value = (opts.volume ?? 1) * spec.defaultVolume;
    src.connect(g); g.connect(busRef);
    src.start();
    return () => {
      try { src.stop(); } catch { /* */ }
    };
  },
};
